import { Stack } from '@startuptools/ui/Stack';
import { Modal } from '@startuptools/ui/Modal';
import { ModalClose } from '@startuptools/ui/ModalClose';
import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { Typography } from '@startuptools/ui/Typography';
import { Alert } from '@startuptools/ui/Alert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Card } from '@startuptools/ui/Card';
import { Link } from '@startuptools/ui/Link';
import { Button } from '@startuptools/ui/Button';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { Box } from '@startuptools/ui/Box';
import { type GqlMeeting, GqlMeetingKind, GqlWarrantProgramKind } from '../../../graphql/base-types.graphql';
import { type GqlDocGenValidateMeetingQuery } from '../../../graphql/react-operations';
import { meetingKindHuman, validatorErrors } from '../utils';
import { Trans, useLingui } from '@lingui/react';
import { useMemo } from 'react';
import { type ArrayElement } from '@startuptools/common/common';
import { isObject } from 'lodash-es';
import { useNavigate } from '../../../components/Navigate';

const McHeader = ({
  mc,
}: {
  mc: ArrayElement<GqlDocGenValidateMeetingQuery['docGenValidateMeeting']['appendices']>['mc'];
}) => {
  if (mc.shareIssue) {
    return (
      <>
        <Trans id="Share Issue" /> - {mc.shareIssue.description}
      </>
    );
  } else if (mc.warrantProgram) {
    switch (mc.warrantProgram.warrantKind) {
      case GqlWarrantProgramKind.Employee: {
        return (
          <>
            <Trans id="QESO" /> - {mc.warrantProgram.name ?? mc.warrantProgram.description}
          </>
        );
      }
      case GqlWarrantProgramKind.Warrant: {
        return (
          <>
            <Trans id="Warrant Program" /> - {mc.warrantProgram.name ?? mc.warrantProgram.description}
          </>
        );
      }
      case GqlWarrantProgramKind.Wise: {
        return (
          <>
            <Trans id="WISE" /> - {mc.warrantProgram.name ?? mc.warrantProgram.description}
          </>
        );
      }
      case GqlWarrantProgramKind.Nice: {
        return (
          <>
            <Trans id="NICE" /> - {mc.warrantProgram.name ?? mc.warrantProgram.description}
          </>
        );
      }
      default: {
        return null;
      }
    }
  } else if (mc.articlesOfAssoc) {
    return <Trans id="Articles Of Assocs" />;
  } else if (mc.authorizationGroup) {
    return <Trans id="Authorisations" />;
  } else {
    throw new Error('unhandled meeting concern');
  }
};

const ValdationErrorLabel = ({ label }: { label: string }) => {
  switch (label) {
    case 'decision':
      return <Trans id="Decision" />;
    case 'companyName':
      return <Trans id="Company Name" />;
    case 'business':
      return <Trans id="Business" />;
    case 'seat':
      return <Trans id="Seat" />;
    case 'summons':
      return <Trans id="Summons" />;
    case 'fiscalYear':
      return <Trans id="Fiscal Year" />;
    case 'board':
      return <Trans id="Board" />;
    case 'reservations':
      return <Trans id="Reservations" />;
    case 'signsRegForm':
      return <Trans id="Documents" />;
    case 'shareChange':
      return <Trans id="Number of shares" />;
    case 'shareLimit':
      return <Trans id="Share limits" />;
  }
};

const McLinkButton = ({
  mc,
  onClose,
}: {
  mc: ArrayElement<GqlDocGenValidateMeetingQuery['docGenValidateMeeting']['appendices']>['mc'];
  onClose: () => void;
}) => {
  const { navigate } = useNavigate();

  if (mc.shareIssue) {
    return (
      <Link underline="none">
        <Button
          variant="outlined"
          onClick={async () => {
            await navigate(`/investments/share-issues/${mc.shareIssueId}/edit`);
            onClose();
          }}
        >
          <Trans id="Go To Share Issue" />
        </Button>
      </Link>
    );
  } else if (mc.warrantProgram) {
    switch (mc.warrantProgram.warrantKind) {
      case GqlWarrantProgramKind.Employee: {
        return (
          <Link underline="none">
            <Button
              variant="outlined"
              onClick={async () => {
                await navigate(`/stock-options/employee-options/${mc.warrantProgramId}/edit/main-step-program`);
                onClose();
              }}
            >
              <Trans id="Go To QESO" />
            </Button>
          </Link>
        );
      }
      case GqlWarrantProgramKind.Warrant: {
        return (
          <Link underline="none">
            <Button
              variant="outlined"
              onClick={async () => {
                await navigate(`/stock-options/warrant-programs/${mc.warrantProgramId}/edit/main-step-program`);
                onClose();
              }}
            >
              <Trans id="Go To Warrant Program" />
            </Button>
          </Link>
        );
      }
      case GqlWarrantProgramKind.Wise: {
        return (
          <Link underline="none">
            <Button
              variant="outlined"
              onClick={async () => {
                await navigate(`/investments/wise-options/${mc.warrantProgramId}/edit/main-step-program`);
                onClose();
              }}
            >
              <Trans id="Go To WISE" />
            </Button>
          </Link>
        );
      }
      case GqlWarrantProgramKind.Nice: {
        return (
          <Link underline="none">
            <Button
              variant="outlined"
              onClick={async () => {
                await navigate(`/investments/nice-options/${mc.warrantProgramId}/edit/main-step-program`);
                onClose();
              }}
            >
              <Trans id="Go To NICE" />
            </Button>
          </Link>
        );
      }
      default: {
        return null;
      }
    }
  } else if (mc.articlesOfAssoc) {
    return (
      <Link underline="none">
        <Button
          variant="outlined"
          onClick={async () => {
            await navigate(`/board-portal/articles-of-assocs/${mc.articlesOfAssocId}/edit`);
            onClose();
          }}
        >
          <Trans id="Go To Articles of Assocs" />
        </Button>
      </Link>
    );
  } else if (mc.authorizationGroup) {
    return (
      <Link underline="none">
        <Button
          variant="outlined"
          onClick={async () => {
            await navigate(`/board-portal/authorization-groups/${mc.authorizationGroupId}/edit`);
            onClose();
          }}
        >
          <Trans id="Go To Authorisation" />
        </Button>
      </Link>
    );
  } else {
    throw new Error('unhandled meeting concern');
  }
};

export const MeetingValidatorErrorDialog = ({
  meeting,
  meetingValidatorErrors,
  showOnlyAppendixErrors = false,
  onClose,
}: {
  meeting: Pick<GqlMeeting, 'id' | 'kind' | 'startTime'>;
  meetingValidatorErrors: GqlDocGenValidateMeetingQuery['docGenValidateMeeting'];
  showOnlyAppendixErrors?: boolean;
  onClose: () => void;
}) => {
  const { i18n } = useLingui();
  const { navigate } = useNavigate();

  const meetingErrors = useMemo(() => {
    return validatorErrors(meetingValidatorErrors.validatorErrors).filter(ve => ve.hasError || !ve.completed);
  }, [meetingValidatorErrors.validatorErrors]);

  return (
    <Modal open onClose={onClose}>
      <ModalDialog>
        <ModalClose />
        <Stack margin={3} gap={3}>
          <Typography level="h4">
            <Trans id="Incomplete meeting" />
          </Typography>
          <Alert startDecorator={<InfoOutlinedIcon />}>
            <Trans id="There are errors in the meeting/or appendices. It's not possible to generate the document before they are resolved" />
          </Alert>
          <Card>
            <>
              {!showOnlyAppendixErrors && meetingErrors.length > 0 && (
                <Stack gap={1}>
                  <Stack direction="row" justifyContent="space-between" justifyItems="center" gap={3}>
                    <Typography level="title-lg">
                      {meetingKindHuman(meeting.kind)}
                      {' - '}
                      <DateFormat locale={i18n.locale}>{meeting.startTime}</DateFormat>
                    </Typography>
                    <Box>
                      <Button
                        variant="outlined"
                        onClick={async () => {
                          await navigate(
                            `/board-portal/${meeting.kind === GqlMeetingKind.BoardMeeting ? 'board-meetings' : 'shareholders-meetings'}/${meeting.id}`,
                          );
                          onClose();
                        }}
                      >
                        <Trans id="Go to Meeting" />
                      </Button>
                    </Box>
                  </Stack>
                  <ul>
                    {meetingErrors.map((ve, i) => {
                      return (
                        <li key={i}>
                          <Stack>
                            <Typography>{ve.error?.[i18n.locale as 'sv' | 'en']?.short}</Typography>
                            <Typography>{ve.error?.[i18n.locale as 'sv' | 'en']?.long}</Typography>
                          </Stack>
                        </li>
                      );
                    })}
                  </ul>
                </Stack>
              )}
              {meetingValidatorErrors.appendices.map(({ mc, validatorStatuses }) => {
                const errors = validatorStatuses.filter(isObject).filter(ve => ve.hasError || !ve.completed);
                if (errors.length === 0) {
                  return null;
                }
                return (
                  <Stack key={mc.id} gap={1}>
                    <Stack direction="row" justifyContent="space-between" justifyItems="center" gap={3}>
                      <Typography level="title-lg">
                        <McHeader mc={mc} />
                      </Typography>
                      <Box>
                        <McLinkButton mc={mc} onClose={onClose} />
                      </Box>
                    </Stack>
                    <ul>
                      {errors.map((ve, i) => {
                        return (
                          <li key={i}>
                            {!ve.completed && (
                              <Typography>
                                <Trans
                                  id="The step {step} is incomplete"
                                  values={{ step: <ValdationErrorLabel label={ve.label!} /> }}
                                />
                              </Typography>
                            )}
                            {ve.error && (
                              <>
                                <Typography level="title-md">
                                  {ve.error?.[i18n.locale as 'sv' | 'en']?.short}
                                </Typography>
                                <Typography>{ve.error?.[i18n.locale as 'sv' | 'en']?.long}</Typography>
                              </>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </Stack>
                );
              })}
            </>
          </Card>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};
